import React, { useState } from 'react';
import { Category } from '../../types';

interface AdminCategoriesProps {
    categories: Category[];
    onAddCategory: (category: Category) => void;
    onUpdateCategory: (category: Category) => void;
    onDeleteCategory?: (categoryId: string) => void;
}

interface Subcategory {
    id: string;
    name: string;
}

const AdminCategories: React.FC<AdminCategoriesProps> = ({
    categories,
    onAddCategory,
    onUpdateCategory,
    onDeleteCategory
}) => {
    const [newCategory, setNewCategory] = useState({ id: '', name: '', icon: 'fa-folder' });
    const [newSubCategory, setNewSubCategory] = useState({
        parentId: categories[0]?.id || '',
        id: '',
        name: ''
    });

    // Edit Modal State
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);
    const [editForm, setEditForm] = useState({ name: '', icon: '' });

    // Subcategory Edit Modal State
    const [editingSubcategory, setEditingSubcategory] = useState<{ category: Category, sub: Subcategory } | null>(null);
    const [editSubForm, setEditSubForm] = useState({ name: '' });

    // Expanded rows for subcategories
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    const toggleExpand = (categoryId: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    const submitCategory = (e: React.FormEvent) => {
        e.preventDefault();
        if (newCategory.id && newCategory.name) {
            onAddCategory({ ...newCategory });
            setNewCategory({ id: '', name: '', icon: 'fa-folder' });
            alert('Category Added Successfully');
        }
    };

    const submitSubCategory = (e: React.FormEvent) => {
        e.preventDefault();
        const parent = categories.find(c => c.id === newSubCategory.parentId);
        if (parent && newSubCategory.id && newSubCategory.name) {
            const updatedParent: Category = {
                ...parent,
                subcategories: [...(parent.subcategories || []), { id: newSubCategory.id, name: newSubCategory.name }]
            };
            onUpdateCategory(updatedParent);
            setNewSubCategory({ ...newSubCategory, id: '', name: '' });
            // Auto-expand to show new subcategory
            setExpandedCategories(prev => new Set(prev).add(parent.id));
            alert('Subcategory Added Successfully');
        }
    };

    const handleEditClick = (category: Category) => {
        setEditingCategory(category);
        setEditForm({ name: category.name, icon: category.icon || 'fa-folder' });
    };

    const handleEditSave = () => {
        if (editingCategory && editForm.name) {
            onUpdateCategory({
                ...editingCategory,
                name: editForm.name,
                icon: editForm.icon
            });
            setEditingCategory(null);
            alert('Category Updated Successfully');
        }
    };

    const handleDeleteClick = (category: Category) => {
        if (confirm(`Are you sure you want to delete "${category.name}"? This will also delete ${category.subcategories?.length || 0} subcategories.`)) {
            if (onDeleteCategory) {
                onDeleteCategory(category.id);
                alert('Category Deleted');
            } else {
                alert('Delete functionality not connected.');
            }
        }
    };

    const handleDeleteSubcategory = (category: Category, subId: string) => {
        const subName = category.subcategories?.find(s => s.id === subId)?.name || subId;
        if (confirm(`Delete subcategory "${subName}"?`)) {
            const updated: Category = {
                ...category,
                subcategories: category.subcategories?.filter(s => s.id !== subId) || []
            };
            onUpdateCategory(updated);
        }
    };

    const handleEditSubcategory = (category: Category, sub: Subcategory) => {
        setEditingSubcategory({ category, sub });
        setEditSubForm({ name: sub.name });
    };

    const handleEditSubSave = () => {
        if (editingSubcategory && editSubForm.name) {
            const updated: Category = {
                ...editingSubcategory.category,
                subcategories: editingSubcategory.category.subcategories?.map(s =>
                    s.id === editingSubcategory.sub.id ? { ...s, name: editSubForm.name } : s
                ) || []
            };
            onUpdateCategory(updated);
            setEditingSubcategory(null);
            alert('Subcategory Updated Successfully');
        }
    };

    return (
        <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                        <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4">Add Category</h3>
                        <form onSubmit={submitCategory} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category ID</label>
                                <input
                                    type="text"
                                    value={newCategory.id}
                                    onChange={e => setNewCategory({ ...newCategory, id: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                    placeholder="e.g. railway"
                                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                                    required
                                />
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Auto-formatted: lowercase, no spaces</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category Name</label>
                                <input
                                    type="text"
                                    value={newCategory.name}
                                    onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
                                    placeholder="e.g. Railways RRB"
                                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Icon Class (FontAwesome)</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newCategory.icon}
                                        onChange={e => setNewCategory({ ...newCategory, icon: e.target.value })}
                                        placeholder="e.g. fa-train"
                                        className="flex-1 p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                                        required
                                    />
                                    <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded flex items-center justify-center text-slate-600 dark:text-slate-300">
                                        <i className={`fas ${newCategory.icon}`}></i>
                                    </div>
                                </div>
                            </div>
                            <button type="submit" className="w-full bg-slate-900 dark:bg-orange-500 text-white py-2 rounded hover:bg-slate-800 dark:hover:bg-orange-600 font-medium">
                                <i className="fas fa-plus mr-2"></i> Add Category
                            </button>
                        </form>
                    </div>

                    <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                        <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4">Add Subcategory</h3>
                        <form onSubmit={submitSubCategory} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Parent Category</label>
                                <select
                                    value={newSubCategory.parentId}
                                    onChange={e => setNewSubCategory({ ...newSubCategory, parentId: e.target.value })}
                                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                                >
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Subcategory ID</label>
                                <input
                                    type="text"
                                    value={newSubCategory.id}
                                    onChange={e => setNewSubCategory({ ...newSubCategory, id: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                    placeholder="e.g. group-d"
                                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Subcategory Name</label>
                                <input
                                    type="text"
                                    value={newSubCategory.name}
                                    onChange={e => setNewSubCategory({ ...newSubCategory, name: e.target.value })}
                                    placeholder="e.g. Group D"
                                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                                    required
                                />
                            </div>
                            <button type="submit" className="w-full bg-orange-500 text-white py-2 rounded hover:bg-orange-600 font-medium">
                                <i className="fas fa-plus mr-2"></i> Add Subcategory
                            </button>
                        </form>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
                            <h3 className="font-bold text-slate-800 dark:text-white">Categories & Subcategories</h3>
                            <span className="text-sm bg-white dark:bg-slate-700 border dark:border-slate-600 px-2 py-1 rounded text-slate-500 dark:text-slate-400">
                                {categories.length} categories • {categories.reduce((acc, c) => acc + (c.subcategories?.length || 0), 0)} subcategories
                            </span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400 text-sm">
                                        <th className="px-6 py-3 font-semibold w-10"></th>
                                        <th className="px-6 py-3 font-semibold">ID</th>
                                        <th className="px-6 py-3 font-semibold">Name</th>
                                        <th className="px-6 py-3 font-semibold">Subcategories</th>
                                        <th className="px-6 py-3 font-semibold text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                    {categories.map((cat) => (
                                        <React.Fragment key={cat.id}>
                                            <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                                <td className="px-6 py-4">
                                                    {cat.subcategories && cat.subcategories.length > 0 && (
                                                        <button
                                                            onClick={() => toggleExpand(cat.id)}
                                                            className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                                        >
                                                            <i className={`fas fa-chevron-${expandedCategories.has(cat.id) ? 'down' : 'right'} text-xs`}></i>
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-sm font-mono text-slate-600 dark:text-slate-400">{cat.id}</td>
                                                <td className="px-6 py-4 font-medium text-slate-800 dark:text-white">
                                                    <div className="flex items-center">
                                                        <i className={`fas ${cat.icon} mr-3 text-orange-500 w-6 text-center`}></i>
                                                        {cat.name}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-sm">
                                                    {cat.subcategories && cat.subcategories.length > 0 ? (
                                                        <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 px-2 py-1 rounded text-xs font-bold">
                                                            {cat.subcategories.length} subcategories
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-400 dark:text-slate-500 italic text-xs">None</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <button
                                                        onClick={() => handleEditClick(cat)}
                                                        className="text-slate-400 hover:text-blue-600 mx-1"
                                                        title="Edit category"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(cat)}
                                                        className="text-slate-400 hover:text-red-600 mx-1"
                                                        title="Delete category"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>

                                            {/* Expanded Subcategories */}
                                            {expandedCategories.has(cat.id) && cat.subcategories && cat.subcategories.map(sub => (
                                                <tr key={`${cat.id}-${sub.id}`} className="bg-slate-50/50 dark:bg-slate-900/50">
                                                    <td className="px-6 py-3"></td>
                                                    <td className="px-6 py-3 text-sm font-mono text-slate-500 dark:text-slate-500">
                                                        <span className="text-slate-300 dark:text-slate-600 mr-2">└</span>
                                                        {sub.id}
                                                    </td>
                                                    <td className="px-6 py-3 text-slate-700 dark:text-slate-300">
                                                        <div className="flex items-center">
                                                            <i className="fas fa-folder-open mr-3 text-slate-400 w-6 text-center text-sm"></i>
                                                            {sub.name}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-3 text-xs text-slate-400">
                                                        Subcategory of {cat.name}
                                                    </td>
                                                    <td className="px-6 py-3 text-right">
                                                        <button
                                                            onClick={() => handleEditSubcategory(cat, sub)}
                                                            className="text-slate-400 hover:text-blue-600 mx-1"
                                                            title="Edit subcategory"
                                                        >
                                                            <i className="fas fa-edit text-sm"></i>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteSubcategory(cat, sub.id)}
                                                            className="text-slate-400 hover:text-red-600 mx-1"
                                                            title="Delete subcategory"
                                                        >
                                                            <i className="fas fa-trash text-sm"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>

                            {categories.length === 0 && (
                                <div className="p-12 text-center text-slate-500 dark:text-slate-400">
                                    <i className="far fa-folder-open text-4xl mb-3 text-slate-300 dark:text-slate-600"></i>
                                    <p>No categories yet. Add your first category!</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Category Modal */}
            {editingCategory && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="p-6">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4">
                                <i className="fas fa-edit mr-2 text-orange-500"></i> Edit Category
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category ID</label>
                                    <input
                                        type="text"
                                        value={editingCategory.id}
                                        disabled
                                        className="w-full p-2 border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-900 text-slate-500 rounded cursor-not-allowed"
                                    />
                                    <p className="text-xs text-slate-400 mt-1">ID cannot be changed</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category Name</label>
                                    <input
                                        type="text"
                                        value={editForm.name}
                                        onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Icon Class</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={editForm.icon}
                                            onChange={e => setEditForm({ ...editForm, icon: e.target.value })}
                                            className="flex-1 p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                                        />
                                        <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded flex items-center justify-center text-orange-500">
                                            <i className={`fas ${editForm.icon}`}></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => setEditingCategory(null)}
                                    className="flex-1 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-50 dark:hover:bg-slate-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleEditSave}
                                    className="flex-1 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Subcategory Modal */}
            {editingSubcategory && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="p-6">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4">
                                <i className="fas fa-folder-open mr-2 text-orange-500"></i> Edit Subcategory
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Parent Category</label>
                                    <input
                                        type="text"
                                        value={editingSubcategory.category.name}
                                        disabled
                                        className="w-full p-2 border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-900 text-slate-500 rounded cursor-not-allowed"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Subcategory ID</label>
                                    <input
                                        type="text"
                                        value={editingSubcategory.sub.id}
                                        disabled
                                        className="w-full p-2 border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-900 text-slate-500 rounded cursor-not-allowed"
                                    />
                                    <p className="text-xs text-slate-400 mt-1">ID cannot be changed</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Subcategory Name</label>
                                    <input
                                        type="text"
                                        value={editSubForm.name}
                                        onChange={e => setEditSubForm({ name: e.target.value })}
                                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => setEditingSubcategory(null)}
                                    className="flex-1 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-50 dark:hover:bg-slate-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleEditSubSave}
                                    className="flex-1 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AdminCategories;
